export const LINE_OAUTH_MESSAGE_TYPE = "line-oauth-complete" as const;
export const LINE_OAUTH_POPUP_STORAGE_KEY = "wizmate.line-oauth-complete";
export const LINE_OAUTH_CONTEXT_STORAGE_KEY = "wizmate.line-oauth-context";

export type LineOAuthPopupResult = "success" | "error";

export type LineOAuthPopupMessage = {
  type: typeof LINE_OAUTH_MESSAGE_TYPE;
  lineConnect: LineOAuthPopupResult;
  errorCode: string | null;
  friendshipStatusChanged: boolean | null;
};

export type LineOAuthPopupSnapshot = LineOAuthPopupMessage & {
  completedAt: number;
};

export type LineOAuthContextSnapshot = {
  returnTo: string;
  returnState?: unknown;
};

function normalizeBoolean(value: string | null): boolean | null {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

function normalizeHashParams(hash: string): string {
  const withoutHash = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!withoutHash) return "";

  const withoutLeadingQuestion = withoutHash.startsWith("?") ? withoutHash.slice(1) : withoutHash;
  const queryIndex = withoutLeadingQuestion.indexOf("?");
  if (queryIndex >= 0) {
    return withoutLeadingQuestion.slice(queryIndex + 1);
  }

  return withoutLeadingQuestion;
}

function readMergedCallbackParam(searchParams: URLSearchParams, hashParams: URLSearchParams, key: string) {
  return searchParams.get(key) ?? hashParams.get(key);
}

export type LineOAuthCallbackParams = {
  code: string | null;
  state: string | null;
  errorCode: string | null;
  friendshipStatusChanged: boolean | null;
};

export function readLineOAuthCallbackParams(search: string, hash = ""): LineOAuthCallbackParams {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(normalizeHashParams(hash));
  const errorCode = readMergedCallbackParam(searchParams, hashParams, "error");

  return {
    code: readMergedCallbackParam(searchParams, hashParams, "code"),
    state: readMergedCallbackParam(searchParams, hashParams, "state"),
    errorCode,
    friendshipStatusChanged: normalizeBoolean(
      readMergedCallbackParam(searchParams, hashParams, "friendship_status_changed"),
    ),
  };
}

export function isLineOAuthPopupMessage(value: unknown): value is LineOAuthPopupMessage {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<LineOAuthPopupMessage>;
  return payload.type === LINE_OAUTH_MESSAGE_TYPE;
}

export function createLineOAuthPopupSnapshot(
  message: LineOAuthPopupMessage,
): LineOAuthPopupSnapshot {
  return {
    ...message,
    completedAt: Date.now(),
  };
}

export function isLineOAuthPopupSnapshot(value: unknown): value is LineOAuthPopupSnapshot {
  if (!isLineOAuthPopupMessage(value)) {
    return false;
  }

  const snapshot = value as Partial<LineOAuthPopupSnapshot>;
  return typeof snapshot.completedAt === "number" && Number.isFinite(snapshot.completedAt);
}

export function readLineOAuthPopupMessage(search: string, hash = ""): LineOAuthPopupMessage {
  const params = readLineOAuthCallbackParams(search, hash);
  const hasCode = Boolean(params.code);

  return {
    type: LINE_OAUTH_MESSAGE_TYPE,
    lineConnect: params.errorCode ? "error" : hasCode ? "success" : "error",
    errorCode: params.errorCode,
    friendshipStatusChanged: params.friendshipStatusChanged,
  };
}

export function writeLineOAuthPopupSnapshot(snapshot: LineOAuthPopupSnapshot) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LINE_OAUTH_POPUP_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // noop
  }
}

export function readLineOAuthPopupSnapshot() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(LINE_OAUTH_POPUP_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    return isLineOAuthPopupSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearLineOAuthPopupSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_POPUP_STORAGE_KEY);
  } catch {
    // noop
  }
}

export function writeLineOAuthContextSnapshot(snapshot: LineOAuthContextSnapshot) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(LINE_OAUTH_CONTEXT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // noop
  }
}

export function readLineOAuthContextSnapshot() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(LINE_OAUTH_CONTEXT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LineOAuthContextSnapshot>;
    if (!parsed || typeof parsed.returnTo !== "string") {
      return null;
    }

    return {
      returnTo: parsed.returnTo,
      returnState: parsed.returnState,
    };
  } catch {
    return null;
  }
}

export function clearLineOAuthContextSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(LINE_OAUTH_CONTEXT_STORAGE_KEY);
  } catch {
    // noop
  }
}

export const LINE_OAUTH_PENDING_CODE_KEY = "wizmate.line-oauth-pending-code";
export const LINE_OAUTH_PENDING_STATE_KEY = "wizmate.line-oauth-pending-state";
export const LINE_OAUTH_PENDING_ERROR_KEY = "wizmate.line-oauth-pending-error";
export const LINE_OAUTH_PENDING_ERROR_STATE_KEY = "wizmate.line-oauth-pending-error-state";
export const LINE_OAUTH_PENDING_REDIRECT_URI_KEY = "wizmate.line-oauth-pending-redirect-uri";
export const LINE_OAUTH_ATTEMPT_STATE_KEY = "wizmate.line-oauth-attempt-state";
export const LINE_OAUTH_ATTEMPT_REDIRECT_URI_KEY = "wizmate.line-oauth-attempt-redirect-uri";

export type LineOAuthAttempt = {
  state: string;
  redirectUri?: string;
};

export type LineOAuthPending =
  | { type: "success"; code: string; state: string; redirectUri?: string }
  | { type: "error"; error: string; state?: string };

export function readLineOAuthAttempt(): LineOAuthAttempt | null {
  if (typeof window === "undefined") return null;

  try {
    const state = window.localStorage.getItem(LINE_OAUTH_ATTEMPT_STATE_KEY);
    if (!state) return null;

    const redirectUri = window.localStorage.getItem(LINE_OAUTH_ATTEMPT_REDIRECT_URI_KEY) ?? undefined;
    return { state, redirectUri };
  } catch {
    return null;
  }
}

export function writeLineOAuthAttempt(params: LineOAuthAttempt) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LINE_OAUTH_ATTEMPT_STATE_KEY, params.state);
    if (params.redirectUri) {
      window.localStorage.setItem(LINE_OAUTH_ATTEMPT_REDIRECT_URI_KEY, params.redirectUri);
    } else {
      window.localStorage.removeItem(LINE_OAUTH_ATTEMPT_REDIRECT_URI_KEY);
    }
  } catch {
    // noop
  }
}

export function clearLineOAuthAttempt() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_ATTEMPT_STATE_KEY);
    window.localStorage.removeItem(LINE_OAUTH_ATTEMPT_REDIRECT_URI_KEY);
  } catch {
    // noop
  }
}

export function readLineOAuthPending(): LineOAuthPending | null {
  if (typeof window === "undefined") return null;

  try {
    const attempt = readLineOAuthAttempt();
    const error = window.localStorage.getItem(LINE_OAUTH_PENDING_ERROR_KEY);
    if (error) {
      const state = window.localStorage.getItem(LINE_OAUTH_PENDING_ERROR_STATE_KEY) ?? undefined;
      if (state && attempt?.state === state) return { type: "error", error, state };
      clearLineOAuthPending();
      return null;
    }

    const code = window.localStorage.getItem(LINE_OAUTH_PENDING_CODE_KEY);
    const state = window.localStorage.getItem(LINE_OAUTH_PENDING_STATE_KEY);
    const redirectUri = window.localStorage.getItem(LINE_OAUTH_PENDING_REDIRECT_URI_KEY) ?? undefined;
    if (code && state && attempt?.state === state) return { type: "success", code, state, redirectUri };
    if (code || state || redirectUri) clearLineOAuthPending();

    return null;
  } catch {
    return null;
  }
}

export function clearLineOAuthPending() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_PENDING_CODE_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_STATE_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_ERROR_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_ERROR_STATE_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_REDIRECT_URI_KEY);
  } catch {
    // noop
  }
}

export function writeLineOAuthPendingSuccess(params: { code: string; state: string; redirectUri?: string }) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_PENDING_ERROR_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_ERROR_STATE_KEY);
    window.localStorage.setItem(LINE_OAUTH_PENDING_CODE_KEY, params.code);
    window.localStorage.setItem(LINE_OAUTH_PENDING_STATE_KEY, params.state);
    if (params.redirectUri) {
      window.localStorage.setItem(LINE_OAUTH_PENDING_REDIRECT_URI_KEY, params.redirectUri);
    } else {
      window.localStorage.removeItem(LINE_OAUTH_PENDING_REDIRECT_URI_KEY);
    }
  } catch {
    // noop
  }
}

export function writeLineOAuthPendingError(error: string, state?: string) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_PENDING_CODE_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_STATE_KEY);
    window.localStorage.removeItem(LINE_OAUTH_PENDING_REDIRECT_URI_KEY);
    window.localStorage.setItem(LINE_OAUTH_PENDING_ERROR_KEY, error);
    if (state) {
      window.localStorage.setItem(LINE_OAUTH_PENDING_ERROR_STATE_KEY, state);
    } else {
      window.localStorage.removeItem(LINE_OAUTH_PENDING_ERROR_STATE_KEY);
    }
  } catch {
    // noop
  }
}

export const LINE_OAUTH_TOKEN_STORAGE_KEY = "wizmate.line-oauth-token";

export function writeLineOAuthTokenSnapshot(token: string) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LINE_OAUTH_TOKEN_STORAGE_KEY, token);
  } catch {
    // noop
  }
}

export function readLineOAuthTokenSnapshot(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage.getItem(LINE_OAUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearLineOAuthTokenSnapshot() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(LINE_OAUTH_TOKEN_STORAGE_KEY);
  } catch {
    // noop
  }
}
